'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useMemo, useState } from 'react'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { Check, Copy } from 'lucide-react'
import { Accordion, Button, ButtonLink } from '@kiao-voice/ui-kit'
import { APP_ROUTES } from '@/lib/app-routes'
import { getWebApiOrigin } from '@/lib/api'

/**
 * Public marketing page for the MCP connector (`/mcp-connector`).
 *
 * Explains the two ways in — the claude.ai connector (OAuth, pick a billing
 * workspace on `/oauth/consent`) and a bearer `kiao_*` API key for Claude Code
 * and other automation — and where the credit comes from. Navbar + footer come
 * from the `(marketing)` layout.
 *
 * The MCP address is derived from `NEXT_PUBLIC_API_BASE_URL` rather than
 * hard-coded, so local, staging and production each show their own host.
 */
export function McpConnectorPage() {
  const { t } = useTranslation('web')
  const mcpUrl = useMemo(() => `${getWebApiOrigin()}/mcp`, [])
  // Config identifiers (JSON keys, the `Bearer` scheme, the `kiao_` key
  // prefix) are protocol literals, not UI copy — they must read identically in
  // every locale, so they are deliberately not translated.
  const configSnippet = useMemo(
    () =>
      [
        '{',
        '  "mcpServers": {',
        '    "kiao-voice": {',
        '      "type": "http",',
        `      "url": "${mcpUrl}",`,
        '      "headers": {',
        '        "Authorization": "Bearer kiao_xxxxxxxx"',
        '      }',
        '    }',
        '  }',
        '}',
      ].join('\n'),
    [mcpUrl]
  )

  const abilities = [
    { key: 'can1', title: t('mcpConnector.can1Title'), body: t('mcpConnector.can1Body') },
    { key: 'can2', title: t('mcpConnector.can2Title'), body: t('mcpConnector.can2Body') },
    { key: 'can3', title: t('mcpConnector.can3Title'), body: t('mcpConnector.can3Body') },
    { key: 'can4', title: t('mcpConnector.can4Title'), body: t('mcpConnector.can4Body') },
    { key: 'can5', title: t('mcpConnector.can5Title'), body: t('mcpConnector.can5Body') },
  ]

  const steps = [
    {
      n: '01',
      title: t('mcpConnector.claudeAiStep1Title'),
      body: t('mcpConnector.claudeAiStep1Body'),
    },
    {
      n: '02',
      title: t('mcpConnector.claudeAiStep2Title'),
      body: t('mcpConnector.claudeAiStep2Body'),
    },
    {
      n: '03',
      title: t('mcpConnector.claudeAiStep3Title'),
      body: t('mcpConnector.claudeAiStep3Body'),
    },
  ]

  const faqs = [
    { value: 'plan', q: t('mcpConnector.faq1Q'), a: t('mcpConnector.faq1A') },
    { value: 'team', q: t('mcpConnector.faq2Q'), a: t('mcpConnector.faq2A') },
    { value: 'disconnect', q: t('mcpConnector.faq3Q'), a: t('mcpConnector.faq3A') },
    { value: 'duration', q: t('mcpConnector.faq4Q'), a: t('mcpConnector.faq4A') },
  ]

  return (
    <div className="mcpc">
      <section className="mcpc-hero">
        <p className="eyebrow">{t('mcpConnector.heroEyebrow')}</p>
        <h1>{t('mcpConnector.heroTitle')}</h1>
        <p className="sub">{t('mcpConnector.heroSub')}</p>
        <div className="cta">
          <ButtonLink as={Link} href={APP_ROUTES.apiKeys}>
            {t('mcpConnector.ctaApiKeys')}
          </ButtonLink>
          <ButtonLink as={Link} variant="secondary" href="/pricing">
            {t('mcpConnector.ctaPricing')}
          </ButtonLink>
        </div>
      </section>

      <section className="mcpc-sec">
        <h2>{t('mcpConnector.whatTitle')}</h2>
        <p className="body">{t('mcpConnector.whatBody')}</p>
      </section>

      <section className="mcpc-sec">
        <h2>{t('mcpConnector.canTitle')}</h2>
        <div className="mcpc-grid">
          {abilities.map((ability) => (
            <div className="mcpc-item" key={ability.key}>
              <h3>{ability.title}</h3>
              <p>{ability.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mcpc-band">
        <div className="in">
          <h2>{t('mcpConnector.claudeAiTitle')}</h2>
          <CopyableCode
            label={t('mcpConnector.serverUrlLabel')}
            value={mcpUrl}
            singleLine
            copyLabel={t('mcpConnector.copyLabel')}
            copiedLabel={t('mcpConnector.copiedLabel')}
          />
          <ol className="mcpc-steps">
            {steps.map((step) => (
              <li key={step.n}>
                <span className="n">{step.n}</span>
                <h3>{step.title}</h3>
                <p>{step.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="mcpc-sec">
        <h2>{t('mcpConnector.claudeCodeTitle')}</h2>
        <p className="body">{t('mcpConnector.claudeCodeBody')}</p>
        <CopyableCode
          label={t('mcpConnector.claudeCodeConfigLabel')}
          value={configSnippet}
          copyLabel={t('mcpConnector.copyLabel')}
          copiedLabel={t('mcpConnector.copiedLabel')}
        />
        <p className="body fine">
          {t('mcpConnector.claudeCodeKeyHint')}{' '}
          <Link href={APP_ROUTES.apiKeys}>{t('mcpConnector.claudeCodeKeyCta')}</Link>
        </p>
      </section>

      <section className="mcpc-sec">
        <h2>{t('mcpConnector.billingTitle')}</h2>
        <p className="body">{t('mcpConnector.billingBody')}</p>
      </section>

      <section className="mcpc-sec">
        <h2>{t('mcpConnector.faqTitle')}</h2>
        <Accordion
          className="mcpc-faq"
          defaultValue="plan"
          items={faqs.map((faq) => ({
            value: faq.value,
            title: faq.q,
            content: <p>{faq.a}</p>,
          }))}
        />
      </section>

      <section className="mcpc-endcta">
        <h2>{t('mcpConnector.endTitle')}</h2>
        <p className="body">{t('mcpConnector.endBody')}</p>
        <ButtonLink as={Link} href={APP_ROUTES.apiKeys}>
          {t('mcpConnector.ctaApiKeys')}
        </ButtonLink>
      </section>
    </div>
  )
}

interface CopyableCodeProps {
  label: string
  value: string
  copyLabel: string
  copiedLabel: string
  /** Render on one line (the connector address) instead of a code block. */
  singleLine?: boolean
}

/**
 * A labelled, selectable snippet with a copy affordance. `navigator.clipboard`
 * is absent on insecure origins and in some in-app browsers; the write is
 * awaited so a rejection surfaces as "not copied" (the text stays selectable)
 * rather than a silently wrong success state.
 */
function CopyableCode({ label, value, copyLabel, copiedLabel, singleLine }: CopyableCodeProps) {
  const [copied, setCopied] = useState(false)

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch (caughtError) {
      reportHandledException(caughtError, {
        source: 'app/(public)/(marketing)/mcp-connector/mcp-connector-page.tsx',
      })
      setCopied(false)
    }
  }, [value])

  return (
    <div className={singleLine ? 'mcpc-code mcpc-code-inline' : 'mcpc-code'}>
      <p className="mcpc-code-label">{label}</p>
      <div className="mcpc-code-row">
        <pre>
          <code>{value}</code>
        </pre>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => void copy()}
          aria-label={copied ? copiedLabel : copyLabel}
        >
          {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
          {copied ? copiedLabel : copyLabel}
        </Button>
      </div>
    </div>
  )
}
